import React, { useState } from "react";
import {
  LayoutDashboard, Factory, Package, Truck, CalendarCheck2, Wallet,
  LogOut, Plus, Download, AlertTriangle, X, Check, ChevronRight,
  Boxes, ShieldCheck
} from "lucide-react";
import { usePbCollection } from "./pbstore";

/* ============================ THEME ============================ */
const C = {
  primary: "#534AB7", primaryBg: "#EEEDFE",
  green: "#1D9E75", greenBg: "#E1F5EE",
  red: "#E24B4A", redBg: "#FCE9E9",
  amber: "#BA7517", amberBg: "#FAEEDA",
  blue: "#185FA5", blueBg: "#E6F1FB",
  plum: "#7B3FA0", plumBg: "#F3E8FB",
  ink: "#1d2330", sub: "#6b7280", line: "#e7e8ee", bg: "#f4f5f8",
};

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap');`;
const display = { fontFamily: "'Archivo', system-ui, sans-serif" };
const body = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };
const mono = { fontFamily: "'IBM Plex Mono', ui-monospace, monospace" };

/* ============================ ROLES / NAV ============================ */
const NAV = [
  { id: "overview",   label: "Overview",   icon: LayoutDashboard, roles: ["admin"] },
  { id: "production", label: "Production", icon: Factory,         roles: ["admin", "line_manager"] },
  { id: "inventory",  label: "Inventory",  icon: Package,         roles: ["admin", "plant_manager"] },
  { id: "logistics",  label: "Logistics",  icon: Truck,           roles: ["admin", "plant_manager"] },
  { id: "attendance", label: "Attendance", icon: CalendarCheck2,  roles: ["admin", "attendance"] },
  { id: "wages",      label: "Wages",      icon: Wallet,          roles: ["admin", "wages"] },
];

/* ============================ PRODUCTION STAGES ============================ */
const STAGES = [
  { id: "fit",       label: "Fit Finalised",          phase: "pre" },
  { id: "sample",    label: "Sample Cut",             phase: "pre" },
  { id: "prewash",   label: "Pre-Wash Details",       phase: "pre" },
  { id: "ssent",     label: "Sample Sent to Wash",    phase: "wash" },
  { id: "swash",     label: "Sample Wash — Pending Approval", phase: "wash" },
  { id: "sealed",    label: "Sample Sealed",          phase: "wash" },
  { id: "cutting",   label: "Production Cutting",      phase: "prod" },
  { id: "stitching", label: "Stitching & Trims",      phase: "prod" },
  { id: "psent",     label: "Production Sent to Wash", phase: "wash2" },
  { id: "pwash",     label: "Prod. Wash — Pending Approval", phase: "wash2" },
  { id: "finishing", label: "Finishing & QC",         phase: "finish" },
  { id: "packing",   label: "Packing & Store",        phase: "finish" },
  { id: "done",      label: "Done",                   phase: "finish" },
];
const SIDX = Object.fromEntries(STAGES.map((s, i) => [s.id, i]));
// two paths: a "sample/major" article is prototyped first (sample wash + seal) before the full lot is cut;
// a "direct" article skips sampling and goes straight to production cutting.
const SAMPLE_FLOW = STAGES.map(s => s.id);
const DIRECT_FLOW = ["fit", "cutting", "stitching", "psent", "pwash", "finishing", "packing", "done"];
const flowOf = (r) => r.mode === "direct" ? DIRECT_FLOW : SAMPLE_FLOW;
const PENDING_WASH = ["swash", "pwash"];            // received from wash, awaiting approval
const SENT_WASH = ["ssent", "psent"];               // out at the washing unit
const DAY = 86400000;
const cutDays = (r) => r.cutDate ? Math.floor((Date.now() - new Date(r.cutDate)) / DAY) : 0;
const isOverdue = (r) => r.cutDate && r.stage !== "done" && cutDays(r) > 10 && (r.delay?.status !== "approved");
const PHASE = {
  pre:   { c: C.primary, bg: C.primaryBg, t: "Pre-Production" },
  wash:  { c: C.amber,   bg: C.amberBg,   t: "Sample Wash" },
  prod:  { c: C.blue,    bg: C.blueBg,    t: "Production" },
  wash2: { c: C.plum,    bg: C.plumBg,    t: "Prod. Wash" },
  finish:{ c: C.green,   bg: C.greenBg,   t: "Finishing" },
};

/* ============================ HELPERS ============================ */
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
let _id = 100; const uid = () => "x" + (++_id);

function downloadCSV(filename, rows) {
  const csv = rows.map(r => r.map(v => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  URL.revokeObjectURL(a.href);
}

/* ============================ SEED DATA ============================ */
const _d = (days) => new Date(Date.now() - days * 86400000).toISOString();
const seedRolls = [
  { id: uid(), rollNo: "01", brand: "Woodland",  article: "Dark Indigo Cargo", fit: "cargo", mode: "sample", stage: "stitching", rejects: 0, fabric: "12oz Denim Fabric", washStyle: "Dark enzyme + tint", cutting: { 30: 50, 32: 80, 34: 60, 36: 30 }, cutDate: _d(12) },
  { id: uid(), rollNo: "02", brand: "Red Chief", article: "Vintage Bootcut",   fit: "bootcut", mode: "sample", stage: "swash", rejects: 1, fabric: "12oz Denim Fabric", washStyle: "Heavy stone wash", washNotes: [{ text: "Shade too light, needs deeper tint", date: _d(2), stage: "Sample Wash" }] },
  { id: uid(), rollNo: "03", brand: "Red Chief", article: "Ecru Straight",     fit: "straight fit", mode: "sample", stage: "pwash", rejects: 0, fabric: "10oz Stretch Denim", washStyle: "Light rinse", cutting: { 32: 100, 34: 80 }, cutDate: _d(4) },
  { id: uid(), rollNo: "04", brand: "Woodland",  article: "Stone Flare",       fit: "flare fit", mode: "direct", stage: "stitching", rejects: 0, fabric: "10oz Stretch Denim", washStyle: "Stone wash", cutting: { 30: 40, 32: 60, 34: 40 }, cutDate: _d(3) },
  { id: uid(), rollNo: "05", brand: "Spykar",    article: "Raw Regular",       fit: "regular", mode: "sample", stage: "fit", rejects: 0 },
  { id: uid(), rollNo: "06", brand: "Red Chief", article: "Acid Slim",         fit: "straight fit", mode: "sample", stage: "sealed", rejects: 0, fabric: "12oz Denim Fabric", washStyle: "Acid wash" },
];
const BRANDS = ["Red Chief", "Woodland", "Spykar", "Killer", "Numero Uno", "Flying Machine"];
const seedRaw = [
  { id: uid(), name: "12oz Denim Fabric", cat: "fabric", supplier: "Arvind Mills", rate: 220, qty: 1400, unit: "m" },
  { id: uid(), name: "10oz Stretch Denim", cat: "fabric", supplier: "Nandan Denim", rate: 195, qty: 880, unit: "m" },
  { id: uid(), name: "Indigo Dye", cat: "chemical", supplier: "Atul Dyes", rate: 540, qty: 80, unit: "kg" },
  { id: uid(), name: "YKK Zippers #5", cat: "trim", supplier: "YKK India", rate: 18, qty: 60, unit: "pcs" },
  { id: uid(), name: "Copper Rivets", cat: "trim", supplier: "Metal Trims Co", rate: 2.5, qty: 9200, unit: "pcs" },
  { id: uid(), name: "Sewing Thread (Ecru)", cat: "trim", supplier: "Coats", rate: 95, qty: 45, unit: "cones" },
];
const seedFinished = [
  { id: uid(), brand: "Woodland", article: "DNM-440", colour: "Dark Indigo", s: { 28: 40, 30: 80, 32: 120, 34: 90, 36: 50, 38: 20 } },
  { id: uid(), brand: "Red Chief", article: "DNM-441", colour: "Stone Blue", s: { 28: 10, 30: 30, 32: 60, 34: 55, 36: 25, 38: 5 } },
];
const seedReject = [
  { id: uid(), brand: "Woodland", article: "DNM-440", colour: "Dark Indigo", size: "32", type: "minor", qty: 6, date: "2026-05-28", note: "Loose hem" },
  { id: uid(), brand: "Red Chief", article: "DNM-441", colour: "Stone Blue", size: "34", type: "major", qty: 3, date: "2026-05-30", note: "Wash patch / shade off" },
];
const seedSales = [
  { id: uid(), kind: "fresh", brand: "Woodland", article: "DNM-440", colour: "Dark Indigo", size: "32", qty: 20, buyer: "Lifestyle Stores", rate: 1200, date: "2026-06-01", status: "pending" },
  { id: uid(), kind: "rejection", brand: "Red Chief", article: "DNM-441", colour: "Stone Blue", size: "34", qty: 2, buyer: "Factory outlet", rate: 450, date: "2026-05-31", status: "approved", dispatchedAt: "2026-06-01" },
];
const COURIER_STATUS = [["pickup", "Pending pickup", "#BA7517", "#FAEEDA"], ["in_transit", "In transit", "#185FA5", "#E6F1FB"], ["out_for_delivery", "Out for delivery", "#7B3FA0", "#F3E8FB"], ["delivered", "Delivered", "#1D9E75", "#E1F5EE"]];
const seedCouriers = [
  { id: uid(), dir: "in",  company: "Arvind Mills", dispatch: "2026-05-29", eta: "2026-06-03", particulars: "12oz denim — 5 rolls", status: "in_transit" },
  { id: uid(), dir: "out", company: "DTDC", dispatch: "2026-05-31", eta: "2026-06-04", particulars: "DNM-440 finished — 200 pcs to Delhi", status: "out_for_delivery" },
  { id: uid(), dir: "out", company: "Blue Dart", dispatch: "2026-05-25", eta: "2026-05-27", particulars: "Samples to buyer (Mumbai)", status: "delivered" },
];
const seedStaff = [
  { id: uid(), name: "Ramesh Kumar", role: "Cutting Master" },
  { id: uid(), name: "Sita Devi", role: "Tailor (Line A)" },
  { id: uid(), name: "Mohan Lal", role: "Wash In-charge" },
  { id: uid(), name: "Geeta Sharma", role: "QC" },
  { id: uid(), name: "Abdul Rahim", role: "Packing" },
];
const seedLabour = [
  { id: uid(), name: "Vijay", method: "upi", upi: "vijay@oksbi" },
  { id: uid(), name: "Salim", method: "cash", upi: "" },
  { id: uid(), name: "Deepak", method: "upi", upi: "deepak123@ybl" },
  { id: uid(), name: "Raju", method: "cash", upi: "" },
];
const seedWeeks = [
  { id: uid(), date: "2026-05-17", type: "kharchi", entries: { } },
  { id: uid(), date: "2026-05-24", type: "wages",   entries: { } },
  { id: uid(), date: "2026-05-31", type: "kharchi", entries: { } },
];
// a few days of seeded attendance history (keyed by date → staffId → P/H/A)
const _dt = (days) => new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
const seedAttendance = {
  [_dt(2)]: { [seedStaff[0].id]: "P", [seedStaff[1].id]: "P", [seedStaff[2].id]: "H", [seedStaff[3].id]: "A", [seedStaff[4].id]: "P" },
  [_dt(1)]: { [seedStaff[0].id]: "P", [seedStaff[1].id]: "H", [seedStaff[2].id]: "P", [seedStaff[3].id]: "P", [seedStaff[4].id]: "P" },
  [_dt(0)]: { [seedStaff[0].id]: "P", [seedStaff[1].id]: "P", [seedStaff[2].id]: "P", [seedStaff[3].id]: "H" },
};

/* ============================ SMALL UI ATOMS ============================ */
const Badge = ({ c, bg, children }) => (
  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
    style={{ color: c, background: bg }}>{children}</span>
);
const Card = ({ children, className = "", style, onClick }) => (
  <div className={"rounded-xl bg-white " + className} style={{ border: `1px solid ${C.line}`, ...style }} onClick={onClick}>{children}</div>
);
const Btn = ({ children, onClick, kind = "primary", icon: Icon, size = "md" }) => {
  const sty = kind === "primary"
    ? { background: C.primary, color: "#fff" }
    : kind === "ghost" ? { background: "#fff", color: C.ink, border: `1px solid ${C.line}` }
    : kind === "danger" ? { background: C.redBg, color: C.red } : { background: C.greenBg, color: C.green };
  return (
    <button onClick={onClick} style={{ ...sty, ...body }}
      className={`inline-flex items-center gap-1.5 rounded-lg font-semibold transition active:scale-95 ${size === "sm" ? "px-2.5 py-1 text-xs" : "px-3.5 py-2 text-sm"}`}>
      {Icon && <Icon size={size === "sm" ? 14 : 16} />}{children}
    </button>
  );
};
const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-xs font-semibold" style={{ color: C.sub }}>
    {label}{children}
  </label>
);
const inputCls = "rounded-lg px-2.5 py-2 text-sm outline-none";
const inputSty = { border: `1px solid ${C.line}`, color: C.ink, ...body };
const PageHead = ({ title, sub, right }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
    <div>
      <h1 className="text-2xl font-extrabold" style={{ ...display, color: C.ink }}>{title}</h1>
      {sub && <p className="text-sm" style={{ color: C.sub }}>{sub}</p>}
    </div>
    {right}
  </div>
);

/* ============================ APP (PROTOTYPE — in-memory data) ============================ */
export default function Dashboard({ session, onLogout }) {
  const allowed = NAV.filter(n => n.roles.includes(session.role));
  const [page, setPage] = useState(() =>
    session.role === "admin" ? "overview" : (allowed[0]?.id || "overview"));

  // Production + Inventory are live from PocketBase; other modules wired in later phases.
  const { items: rolls, create: createRoll, update: updateRoll } = usePbCollection("rolls", { sort: "created" });
  const rawStore = usePbCollection("raw_materials");
  const finStore = usePbCollection("finished_stock");
  const rejStore = usePbCollection("rejects");
  const raw = rawStore.items, finished = finStore.items, rejects = rejStore.items;
  const courStore = usePbCollection("couriers", { sort: "-created" });
  const couriers = courStore.items;
  const staffStore = usePbCollection("staff", { sort: "created" });
  const staff = staffStore.items;
  const attStore = usePbCollection("attendance_days", { sort: "date" });
  const attendance = Object.fromEntries(attStore.items.map(r => [r.date, r.entries || {}]));
  const markAttendance = (date, sid, val) => {
    const rec = attStore.items.find(r => r.date === date);
    if (rec) return attStore.update(rec.id, { entries: { ...(rec.entries || {}), [sid]: val } });
    return attStore.create({ date, entries: { [sid]: val } });
  };
  const labourStore = usePbCollection("labour", { sort: "created" });
  const labour = labourStore.items;
  const weekStore = usePbCollection("wage_weeks", { sort: "created" });
  const weeks = weekStore.items;
  const salesStore = usePbCollection("sales", { sort: "-created" });
  const sales = salesStore.items;

  const [drill, setDrill] = useState(null); // {title, fields:[[label,val]], tone}

  const props = { session, drill: setDrill,
    rolls, createRoll, updateRoll,
    raw, createRaw: rawStore.create,
    finished, createFinished: finStore.create, updateFinished: finStore.update,
    rejects, createReject: rejStore.create, updateReject: rejStore.update,
    couriers, createCourier: courStore.create, updateCourier: courStore.update,
    staff, createStaff: staffStore.create, attendance, markAttendance,
    labour, createLabour: labourStore.create,
    weeks, createWeek: weekStore.create, updateWeek: weekStore.update,
    sales, createSale: salesStore.create, updateSale: salesStore.update, removeSale: salesStore.remove };

  return (
    <div className="min-h-screen w-full" style={{ background: C.bg, ...body, color: C.ink }}>
      <style>{FONTS}</style>

      {/* top bar */}
      <header className="sticky top-0 z-20 flex items-center justify-between px-4 py-3"
        style={{ background: "#fff", borderBottom: `1px solid ${C.line}` }}>
        <div className="flex items-center gap-3">
          <div className="text-xl font-extrabold tracking-tight" style={display}>
            denim<span style={{ color: C.primary }}>erp</span>
          </div>
          <span className="hidden sm:inline text-xs font-semibold rounded-full px-2 py-0.5"
            style={{ background: C.primaryBg, color: C.primary }}>Operations Portal</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right leading-tight">
            <div className="text-sm font-semibold">{session.name}</div>
            <div className="text-xs capitalize" style={{ color: C.sub }}>{session.role.replace("_", " ")}</div>
          </div>
          <button onClick={onLogout} title="Logout"
            className="rounded-lg p-2" style={{ border: `1px solid ${C.line}` }}>
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-4 p-4">
        {/* sidebar */}
        <aside className="hidden md:block w-52 shrink-0">
          <nav className="flex flex-col gap-1">
            {allowed.map(n => {
              const on = page === n.id;
              return (
                <button key={n.id} onClick={() => setPage(n.id)}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-semibold transition"
                  style={on ? { background: C.primary, color: "#fff" } : { color: C.ink }}>
                  <n.icon size={17} /> {n.label}
                </button>
              );
            })}
          </nav>
          {session.role === "admin" && (
            <div className="mt-4 rounded-lg p-3 text-xs" style={{ background: C.primaryBg, color: C.primary }}>
              <ShieldCheck size={14} className="inline mr-1" />
              Admin view — full visibility across all dashboards. Click any row to drill into details.
            </div>
          )}
        </aside>

        {/* mobile nav */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-20 flex justify-around py-1.5"
          style={{ background: "#fff", borderTop: `1px solid ${C.line}` }}>
          {allowed.map(n => (
            <button key={n.id} onClick={() => setPage(n.id)} className="flex flex-col items-center gap-0.5 px-2 py-1 text-xs font-semibold"
              style={{ color: page === n.id ? C.primary : C.sub }}>
              <n.icon size={18} />{n.label}
            </button>
          ))}
        </div>

        {/* main */}
        <main className="min-w-0 flex-1 pb-20 md:pb-0">
          {page === "overview"   && <Overview {...props} go={setPage} />}
          {page === "production" && <Production {...props} />}
          {page === "inventory"  && <Inventory {...props} />}
          {page === "logistics"  && <Logistics {...props} />}
          {page === "attendance" && <Attendance {...props} />}
          {page === "wages"      && <Wages {...props} />}
        </main>
      </div>

      {drill && <Drawer data={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

/* ============================ DRILL DRAWER ============================ */
function Drawer({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end" style={{ background: "rgba(20,22,30,.4)" }} onClick={onClose}>
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-5" style={body} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            {data.tag && <Badge c={data.tag.c} bg={data.tag.bg}>{data.tag.t}</Badge>}
            <h2 className="mt-1 text-xl font-extrabold" style={{ ...display, color: C.ink }}>{data.title}</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5" style={{ border: `1px solid ${C.line}` }}><X size={16} /></button>
        </div>
        <div className="flex flex-col gap-2.5">
          {data.fields.map(([k, v], i) => (
            <div key={i} className="flex justify-between gap-4 rounded-lg px-3 py-2" style={{ background: C.bg }}>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.sub }}>{k}</span>
              <span className="text-sm font-semibold text-right" style={{ color: C.ink }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================ OVERVIEW (admin) ============================ */
function Overview({ rolls, raw, couriers, attendance, weeks, labour, finished, updateFinished, rejects, updateReject, sales, updateSale, removeSale, drill, go }) {
  const low = raw.filter(r => r.qty < 100);
  const inProd = rolls.filter(r => !["done"].includes(r.stage)).length;
  const pendingC = couriers.filter(c => c.status !== "delivered").length;
  const todayAtt = attendance[today()] || {};
  const present = Object.values(todayAtt).filter(v => v === "P").length;
  const lastWeek = weeks[weeks.length - 1] || { type: "wages" };  // guard: empty weeks must not crash the dashboard

  const pendingWash = rolls.filter(r => PENDING_WASH.includes(r.stage));
  const approvedNotCut = rolls.filter(r => r.stage === "sealed");   // sample wash approved, lot not yet cut
  const overdue = rolls.filter(isOverdue);
  const pendingSales = (sales || []).filter(s => s.status === "pending");

  const approveSale = (s) => updateSale(s.id, { status: "approved", dispatchedAt: today() });
  const rejectSale = (s) => {
    if (s.kind === "fresh") { const f = finished.find(x => x.id === s.srcId); if (f) updateFinished(f.id, { s: { ...f.s, [s.size]: (f.s[s.size] || 0) + s.qty } }); }
    else { const j = rejects.find(x => x.id === s.srcId); if (j) updateReject(j.id, { qty: j.qty + s.qty }); }
    removeSale(s.id);
  };

  const stats = [
    { t: "In production", v: inProd, c: C.blue, bg: C.blueBg, to: "production" },
    { t: "Pending wash approval", v: pendingWash.length, c: C.red, bg: C.redBg, to: "production" },
    { t: "Approved, not cut", v: approvedNotCut.length, c: C.amber, bg: C.amberBg, to: "production" },
    { t: "Overdue >10 days", v: overdue.length, c: C.red, bg: C.redBg, to: "production" },
    { t: "Sales to approve", v: pendingSales.length, c: C.plum, bg: C.plumBg, to: "inventory" },
    { t: "Low-stock materials", v: low.length, c: C.red, bg: C.redBg, to: "inventory" },
    { t: "Couriers pending", v: pendingC, c: C.amber, bg: C.amberBg, to: "logistics" },
    { t: "Present today", v: `${present}/${Object.keys(todayAtt).length || "—"}`, c: C.green, bg: C.greenBg, to: "attendance" },
    { t: `Next payout (${lastWeek.type})`, v: labour.length + " lab.", c: C.plum, bg: C.plumBg, to: "wages" },
  ];
  const rollDrill = (r) => drill({ title: `Roll ${r.rollNo} — ${r.article}`, tag: PHASE[STAGES[SIDX[r.stage]].phase],
    fields: [["Brand", r.brand || "—"], ["Stage", STAGES[SIDX[r.stage]].label], ["Mode", r.mode], ["Fabric", r.fabric || "—"], ["Wash style", r.washStyle || "—"], ["Pieces cut", cutTotal(r.cutting)], ["Days since cut", r.cutDate ? cutDays(r) + " d" : "—"], ["Wash rejects", r.rejects || 0]] });

  return (
    <div>
      <PageHead title="Overview" sub="Everything across the plant — admin only" right={<Btn kind="ghost" size="sm" onClick={() => go("production")}>Open production</Btn>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {stats.map((s, i) => (
          <Card key={i} className="p-4 cursor-pointer" onClick={() => go(s.to)}
            style={s.c === C.red && s.v ? { borderColor: C.red, background: C.redBg } : undefined}>
            <div className="text-3xl font-extrabold" style={{ ...display, color: s.c }}>{s.v}</div>
            <div className="text-xs font-semibold mt-1" style={{ color: C.sub }}>{s.t} →</div>
          </Card>
        ))}
      </div>

      {/* PRODUCTION ALERTS */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Card className="p-4" style={{ borderColor: pendingWash.length ? C.red : C.line }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold" style={{ ...display, color: C.red }}>⏳ Pending for Wash Approval</h3>
            <Btn kind="ghost" size="sm" onClick={() => go("production")}>Open →</Btn>
          </div>
          {pendingWash.length === 0 ? <Empty small label="Nothing waiting" /> :
            pendingWash.map(r => (
              <Row key={r.id} onClick={() => rollDrill(r)}>
                <span className="font-semibold">Roll {r.rollNo} — {r.article}</span>
                <Badge c={C.red} bg={C.redBg}>{r.stage === "swash" ? "Sample" : "Production"}</Badge>
              </Row>
            ))}
        </Card>
        <Card className="p-4" style={{ borderColor: approvedNotCut.length ? C.amber : C.line }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold" style={{ ...display, color: C.amber }}>✅ Wash Approved — Not Yet Cut</h3>
            <Btn kind="ghost" size="sm" onClick={() => go("production")}>Open →</Btn>
          </div>
          {approvedNotCut.length === 0 ? <Empty small label="All approved samples cut" /> :
            approvedNotCut.map(r => (
              <Row key={r.id} onClick={() => rollDrill(r)}>
                <span className="font-semibold">Roll {r.rollNo} — {r.article}</span>
                <Badge c={C.amber} bg={C.amberBg}>cut pending</Badge>
              </Row>
            ))}
        </Card>
        <Card className="p-4" style={{ borderColor: overdue.length ? C.red : C.line }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold" style={{ ...display, color: C.red }}>⏰ Overdue (&gt;10 days since cut)</h3>
            <Btn kind="ghost" size="sm" onClick={() => go("production")}>Open →</Btn>
          </div>
          {overdue.length === 0 ? <Empty small label="Nothing overdue" /> :
            overdue.map(r => (
              <Row key={r.id} onClick={() => rollDrill(r)}>
                <span className="font-semibold">Roll {r.rollNo} — {r.article}</span>
                <Badge c={C.red} bg={C.redBg}>{cutDays(r)}d · {r.delay?.status === "responded" ? "reason in" : r.delay?.status === "raised" ? "queried" : "review"}</Badge>
              </Row>
            ))}
        </Card>
      </div>

      {/* SALES PENDING APPROVAL */}
      <Card className="p-4 mb-4" style={{ borderColor: pendingSales.length ? C.plum : C.line }}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold" style={{ ...display, color: C.plum }}>🧾 Sales — Pending Your Approval</h3>
          <Btn kind="ghost" size="sm" onClick={() => go("inventory")}>Open sales →</Btn>
        </div>
        {pendingSales.length === 0 ? <Empty small label="No sales waiting for approval" /> : (
          <div className="flex flex-col gap-2">
            {pendingSales.map(s => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2" style={{ background: C.bg }}>
                <span className="text-sm">
                  <Badge c={s.kind === "fresh" ? C.green : C.red} bg={s.kind === "fresh" ? C.greenBg : C.redBg}>{s.kind}</Badge>
                  <span className="font-semibold ml-2">{s.brand} · {s.article}</span>
                  <span style={{ color: C.sub }}> — sz {s.size} × {s.qty} → {s.buyer || "buyer"} ({inr(s.rate * s.qty)})</span>
                </span>
                <span className="flex gap-2">
                  <Btn size="sm" kind="success" icon={Check} onClick={() => approveSale(s)}>Approve &amp; dispatch</Btn>
                  <Btn size="sm" kind="danger" icon={X} onClick={() => rejectSale(s)}>Reject</Btn>
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold" style={display}>Low stock — needs reorder</h3>
            <Btn kind="ghost" size="sm" onClick={() => go("inventory")}>Open inventory</Btn>
          </div>
          {low.length === 0 ? <Empty small label="All materials above threshold" /> :
            low.map(r => (
              <Row key={r.id} onClick={() => drill({ title: r.name, tag: { c: C.red, bg: C.redBg, t: "LOW STOCK" },
                fields: [["Supplier", r.supplier], ["Rate", inr(r.rate) + "/" + r.unit], ["Quantity", `${r.qty} ${r.unit}`], ["Threshold", "100"]] })}>
                <span className="font-semibold">{r.name}</span>
                <Badge c={C.red} bg={C.redBg}>{r.qty} {r.unit}</Badge>
              </Row>
            ))}
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold" style={display}>Couriers in transit</h3>
            <Btn kind="ghost" size="sm" onClick={() => go("logistics")}>Open logistics</Btn>
          </div>
          {couriers.filter(c => c.status !== "delivered").map(c => (
            <Row key={c.id} onClick={() => drill({ title: c.company, tag: dirTag(c.dir),
              fields: [["Direction", c.dir === "in" ? "Incoming" : "Outgoing"], ["Dispatched", fmtDate(c.dispatch)], ["Expected", fmtDate(c.eta)], ["Particulars", c.particulars]] })}>
              <span><Badge {...dirTag(c.dir)}>{c.dir === "in" ? "IN" : "OUT"}</Badge> <span className="font-semibold ml-1">{c.company}</span></span>
              <span className="text-xs" style={{ color: C.sub }}>ETA {fmtDate(c.eta)}</span>
            </Row>
          ))}
        </Card>
      </div>
    </div>
  );
}
const dirTag = (d) => d === "in" ? { c: C.green, bg: C.greenBg, t: "INCOMING" } : { c: C.blue, bg: C.blueBg, t: "OUTGOING" };
const Row = ({ children, onClick }) => (
  <button onClick={onClick} className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition hover:opacity-80"
    style={{ borderBottom: `1px solid ${C.line}` }}>{children}</button>
);
const Empty = ({ label, small }) => (
  <div className={`flex flex-col items-center justify-center gap-1 text-center ${small ? "py-6" : "py-14"}`} style={{ color: C.sub }}>
    <Boxes size={small ? 22 : 34} opacity={0.4} /><div className="text-sm">{label}</div>
  </div>
);

/* ============================ PRODUCTION ============================ */
const FITS = ["straight fit", "bootcut", "cargo", "regular", "flare fit", "bootcut/straight", "bootcut/flare"];
const PSIZES = ["28", "30", "32", "34", "36", "38"];
const cutTotal = (c = {}) => PSIZES.reduce((a, s) => a + (+c[s] || 0), 0);

function Production({ rolls, createRoll, updateRoll, raw, finished, createFinished, updateFinished, drill, session }) {
  const canEdit = session.role === "admin" || session.role === "line_manager";
  const [sel, setSel] = useState(null);
  const [adding, setAdding] = useState(false);
  const fabricItems = (raw || []).filter(r => r.cat === "fabric");
  const fabrics = fabricItems.map(r => r.name);
  const fabricOpts = fabricItems.map(r => ({ value: r.name, label: `${r.name} — ${r.qty} ${r.unit} in stock` }));
  const brandSuggest = [...new Set([...BRANDS, ...rolls.map(r => r.brand).filter(Boolean)])];
  const [af, setAf] = useState({ brand: "", rollNo: "", article: "", fit: "straight fit", fabric: fabrics[0] || "", mode: "sample" });
  const [brand, setBrand] = useState("all");

  const brandList = [...new Set(rolls.map(r => r.brand || "Unbranded"))];
  const shown = brand === "all" ? rolls : rolls.filter(r => (r.brand || "Unbranded") === brand);

  const upd = (id, patch) => updateRoll(id, patch);
  const advance = (r, to, extra = {}) => upd(r.id, { stage: to, ...extra });
  const fidx = (r, id = r.stage) => flowOf(r).indexOf(id);
  const next = (r) => flowOf(r)[Math.min(fidx(r) + 1, flowOf(r).length - 1)];
  const prog = (r) => Math.round((Math.max(fidx(r), 0) / (flowOf(r).length - 1)) * 100);
  const atCutting = (r) => fidx(r) >= fidx(r, "cutting");
  // wash style is set by staff ONLY just before the article is sent to wash (i.e. the next step is "Sent to Wash")
  const preSend = (r) => SENT_WASH.includes(next(r));

  const washApprove = (r) => advance(r, r.stage === "swash" ? "sealed" : "finishing");
  const washReject = (r, remark) => {
    const note = { text: remark || "(no remark)", date: new Date().toISOString(), stage: STAGES[SIDX[r.stage]].label };
    advance(r, r.stage === "swash" ? "ssent" : "psent", { rejects: (r.rejects || 0) + 1, washNotes: [...(r.washNotes || []), note] });
  };
  // auto-add cutting to finished stock when production completes
  const completeProd = (r) => {
    advance(r, "done");
    const ex = finished.find(f => f.article === r.article);
    if (ex) {
      const merged = { ...ex.s };
      PSIZES.forEach(s => { merged[s] = (merged[s] || 0) + (r.cutting?.[s] || 0); });
      updateFinished(ex.id, { s: merged });
    } else {
      createFinished({ brand: r.brand || "—", article: r.article, colour: r.fabric || "—", s: { ...(r.cutting || {}) } });
    }
  };
  // flag if wash style is changed after prototype sealing
  const setWash = (r, v) => {
    const postSeal = r.mode === "sample" && SIDX[r.stage] > SIDX["sealed"];
    upd(r.id, { washStyle: v, ...(postSeal ? { washEditedPostSeal: true } : {}) });
  };

  const addArticle = () => {
    if (!af.rollNo || !af.article) return;
    createRoll({ brand: af.brand.trim() || "Unbranded", rollNo: af.rollNo, article: af.article, fit: af.fit, fabric: af.fabric, mode: af.mode, stage: "fit", rejects: 0, cutting: {}, washNotes: [], washRefs: [] });
    if (af.brand.trim()) setBrand(af.brand.trim());
    setAf({ brand: af.brand, rollNo: "", article: "", fit: "straight fit", fabric: fabrics[0] || "", mode: "sample" });
    setAdding(false);
  };

  return (
    <div>
      <PageHead title="Production Tracker"
        sub={`${rolls.length} articles · ${rolls.filter(r=>r.stage==="done").length} done · ${rolls.filter(r=>["swash","pwash"].includes(r.stage)).length} awaiting wash review`}
        right={canEdit && <Btn icon={Plus} onClick={() => setAdding(a => !a)}>New article</Btn>} />

      {adding && canEdit && (
        <Card className="mb-4 p-4">
          <div className="mb-2 font-bold" style={display}>Add a new article / roll</div>
          <div className="text-xs font-semibold uppercase mb-1.5" style={{ color: C.sub }}>Step 1 — brand &amp; article</div>
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <Field label="Brand">
              <input list="brand-list" className={inputCls} style={{ ...inputSty, width: 160 }} value={af.brand} onChange={e => setAf({ ...af, brand: e.target.value })} placeholder="e.g. Red Chief" />
              <datalist id="brand-list">{brandSuggest.map(b => <option key={b} value={b} />)}</datalist>
            </Field>
            <Field label="Roll #"><input className={inputCls} style={{ ...inputSty, width: 80 }} value={af.rollNo} onChange={e => setAf({ ...af, rollNo: e.target.value })} placeholder="06" /></Field>
            <Field label="Article name"><input className={inputCls} style={{ ...inputSty, width: 170 }} value={af.article} onChange={e => setAf({ ...af, article: e.target.value })} placeholder="e.g. Acid Wash Slim" /></Field>
            <Field label="Fit"><select className={inputCls} style={{ ...inputSty, width: 140 }} value={af.fit} onChange={e => setAf({ ...af, fit: e.target.value })}>{FITS.map(f => <option key={f}>{f}</option>)}</select></Field>
            <Field label="Fabric (from inventory)">
              <select className={inputCls} style={{ ...inputSty, width: 230 }} value={af.fabric} onChange={e => setAf({ ...af, fabric: e.target.value })}>
                {fabricOpts.length === 0 && <option value="">— no fabric in inventory —</option>}
                {fabricOpts.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="text-xs font-semibold uppercase mb-1.5" style={{ color: C.sub }}>Step 2 — how is this article produced?</div>
          <div className="flex gap-2">
            {[["sample", "Make a sample first", "Prototype piece → wash → review → seal → then cut the lot"],
              ["direct", "Direct production", "Skip sampling — cut the full lot straight away"]].map(([m, t, d]) => (
              <button key={m} onClick={() => setAf({ ...af, mode: m })}
                className="flex-1 rounded-lg p-3 text-left transition"
                style={af.mode === m ? { border: `2px solid ${C.primary}`, background: C.primaryBg } : { border: `1px solid ${C.line}`, background: "#fff" }}>
                <div className="text-sm font-bold" style={{ color: af.mode === m ? C.primary : C.ink }}>{t}</div>
                <div className="text-xs mt-0.5" style={{ color: C.sub }}>{d}</div>
              </button>
            ))}
          </div>
          <div className="mt-3"><Btn icon={Check} onClick={addArticle}>Create article</Btn></div>
        </Card>
      )}

      {/* BRAND SELECTOR — click a brand to see its production */}
      <div className="mb-4">
        <div className="text-xs font-semibold uppercase mb-1.5" style={{ color: C.sub }}>Brands — tap one to view its production</div>
        <div className="flex flex-wrap gap-2">
          <BrandCard label="All brands" sub={`${rolls.length} articles`} active={brand === "all"} onClick={() => setBrand("all")} />
          {brandList.map(b => {
            const arts = rolls.filter(r => (r.brand || "Unbranded") === b);
            const pend = arts.filter(r => PENDING_WASH.includes(r.stage)).length;
            return <BrandCard key={b} label={b} sub={`(${arts.map(a => a.article).join(", ")})`} count={arts.length} alert={pend} active={brand === b} onClick={() => setBrand(b)} />;
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        {shown.map(r => {
          const ph = PHASE[STAGES[SIDX[r.stage]].phase];
          const open = sel === r.id;
          const cutReady = atCutting(r);
          const pending = PENDING_WASH.includes(r.stage);
          const overdue = isOverdue(r);
          const saveCut = (cut) => r.stage === "cutting"
            ? advance(r, next(r), { cutting: cut, cutDate: r.cutDate || new Date().toISOString() })
            : upd(r.id, { cutting: cut });
          return (
            <Card key={r.id} className="p-4" style={pending ? { borderColor: C.red, background: C.redBg } : undefined}>
              <div className="cursor-pointer" onClick={() => setSel(open ? null : r.id)}>
                <div className="flex items-center justify-between">
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-wide" style={{ color: C.primary }}>{r.brand || "Unbranded"}</span>
                    <span className="font-bold" style={display}>Roll {r.rollNo} — {r.article}</span>
                  </span>
                  <span className="flex gap-1.5">
                    <Badge c={r.mode === "direct" ? C.green : C.primary} bg={r.mode === "direct" ? C.greenBg : C.primaryBg}>{r.mode === "direct" ? "Direct" : "Sample"}</Badge>
                    <Badge c={C.ink} bg="#fff">{r.fit}</Badge>
                  </span>
                </div>
                <div className="my-2 h-2 w-full rounded-full" style={{ background: pending ? "#fff" : C.line }}>
                  <div className="h-2 rounded-full" style={{ width: prog(r) + "%", background: r.rejects ? C.red : ph.c }} />
                </div>
                <div className="flex items-center justify-between">
                  <Badge c={pending ? C.red : ph.c} bg={pending ? "#fff" : ph.bg}>{pending ? "⏳ Pending wash approval" : STAGES[SIDX[r.stage]].label}</Badge>
                  <span className="flex items-center gap-2">
                    {overdue && <span className="text-xs font-semibold" style={{ color: C.red }}>⏰ {cutDays(r)}d</span>}
                    {r.washEditedPostSeal && <span className="text-xs font-semibold" style={{ color: C.amber }}>⚠ wash edited</span>}
                    {cutTotal(r.cutting) > 0 && <span className="text-xs font-semibold" style={{ color: C.blue }}>{cutTotal(r.cutting)} pcs</span>}
                    {r.rejects > 0 && <span className="text-xs font-semibold" style={{ color: C.red }}>⚠ {r.rejects}</span>}
                  </span>
                </div>
              </div>

              {open && (
                <div className="mt-3 pt-3 flex flex-col gap-3" style={{ borderTop: `1px solid ${C.line}` }}>
                  <button onClick={() => drill({ title: `Roll ${r.rollNo}`, tag: { c: ph.c, bg: ph.bg, t: ph.t },
                    fields: [["Brand", r.brand || "Unbranded"], ["Article", r.article], ["Mode", r.mode === "direct" ? "Direct production" : "Sample first"], ["Fit", r.fit], ["Fabric", r.fabric || "—"], ["Wash style", r.washStyle || "—"], ["Coin pocket", r.coinPocket || "—"], ["Stage", STAGES[SIDX[r.stage]].label], ["Pieces cut", cutTotal(r.cutting) + " pcs"], ["Days since cut", r.cutDate ? cutDays(r) + " d" : "—"], ["Wash rejects", r.rejects || 0], ["Wash remarks", (r.washNotes || []).length]] })}
                    className="text-xs font-semibold text-left" style={{ color: C.primary }}>View full details →</button>

                  {/* MODE — changeable only while still at the first stage */}
                  {canEdit && r.stage === "fit" && (
                    <div>
                      <div className="text-xs font-semibold uppercase mb-1.5" style={{ color: C.sub }}>Production mode (set before starting)</div>
                      <div className="flex gap-2">
                        {[["sample", "Sample first"], ["direct", "Direct production"]].map(([m, t]) => (
                          <button key={m} onClick={() => upd(r.id, { mode: m })} className="rounded-lg px-3 py-1.5 text-xs font-semibold"
                            style={r.mode === m ? { background: C.primary, color: "#fff" } : { background: C.bg, color: C.sub }}>{t}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* DETAILS (always editable) */}
                  <div>
                    <div className="text-xs font-semibold uppercase mb-1.5" style={{ color: C.sub }}>Article details</div>
                    <div className="grid grid-cols-2 gap-2">
                      <DField key={r.id+"fab"} label="Fabric used" disabled={!canEdit} value={r.fabric} options={fabricOpts} onSave={v => upd(r.id, { fabric: v })} />
                      <DField key={r.id+"thr"} label="Thread colour" disabled={!canEdit} value={r.thread} onSave={v => upd(r.id, { thread: v })} placeholder="e.g. Ecru" />
                      <DField key={r.id+"coin"} label="Coin pocket" disabled={!canEdit} value={r.coinPocket} onSave={v => upd(r.id, { coinPocket: v })} placeholder="e.g. Watch pocket" />
                      <DField key={r.id+"belt"} label="Belt embroidery" disabled={!canEdit} value={r.beltEmb} onSave={v => upd(r.id, { beltEmb: v })} placeholder="Design / text" />
                    </div>
                  </div>

                  {/* CUTTING — full lot. In sample mode unlocks only after sample approval & seal. */}
                  {cutReady ? (
                    <CutEntry key={r.id+"cut"} roll={r} canEdit={canEdit} onSave={saveCut} />
                  ) : (
                    <div className="rounded-lg px-3 py-2 text-xs" style={{ background: C.bg, color: C.sub }}>
                      ✂ {r.mode === "direct"
                        ? "Advance to Production Cutting to enter the lot quantities."
                        : "Only the sample piece is cut for now. Full-lot cutting unlocks once the sample wash is approved & sealed."}
                    </div>
                  )}

                  {/* WASH — staff set the wash style ONLY just before sending to wash */}
                  {r.washEditedPostSeal && (
                    <div className="rounded-lg px-3 py-1.5 text-xs font-semibold" style={{ background: C.amberBg, color: C.amber }}>
                      ⚠ Wash edited post prototype sealing
                    </div>
                  )}
                  {(preSend(r) || r.washStyle) && (
                    <div>
                      <div className="text-xs font-semibold uppercase mb-1.5" style={{ color: C.sub }}>Wash{preSend(r) ? " — set before sending to wash" : ""}</div>
                      {preSend(r) ? (
                        <div className="flex flex-col gap-2">
                          <DField key={r.id+"wsh"} label="Wash style" disabled={!canEdit} value={r.washStyle} onSave={v => setWash(r, v)} placeholder="e.g. Light enzyme wash" />
                          {r.washStyle
                            ? <RefPics roll={r} canEdit={canEdit} onAdd={u => upd(r.id, { washRefs: [...(r.washRefs || []), u] })} onRemove={i => upd(r.id, { washRefs: (r.washRefs || []).filter((_, j) => j !== i) })} />
                            : <div className="text-xs" style={{ color: C.sub }}>Enter a wash style to attach reference pictures.</div>}
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <div className="rounded-lg px-3 py-2 text-sm" style={{ background: C.bg }}>
                            <span className="text-xs font-semibold" style={{ color: C.sub }}>Wash style: </span><b>{r.washStyle}</b>
                          </div>
                          {(r.washRefs || []).length > 0 && <RefPics roll={r} canEdit={false} onAdd={() => {}} onRemove={() => {}} />}
                        </div>
                      )}
                    </div>
                  )}

                  {/* WASH REMARKS HISTORY */}
                  {(r.washNotes || []).length > 0 && (
                    <div>
                      <div className="text-xs font-semibold uppercase mb-1.5" style={{ color: C.red }}>Washing remarks (rejections)</div>
                      <div className="flex flex-col gap-1.5">
                        {[...r.washNotes].reverse().map((n, i) => (
                          <div key={i} className="rounded-lg px-3 py-1.5 text-xs" style={{ background: C.redBg }}>
                            <span className="font-semibold" style={{ color: C.red }}>{n.stage}</span> — {n.text}
                            <span className="ml-1" style={{ color: C.sub }}>· {fmtDate(n.date)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* OVERDUE / DELAY workflow */}
                  {(overdue || (r.delay && r.delay.status && r.delay.status !== "none")) &&
                    <DelayBlock roll={r} session={session} onUpdate={upd} />}

                  {/* STAGE ACTION */}
                  <div>
                    {!canEdit ? <div className="text-xs" style={{ color: C.sub }}>Read-only (line manager edits here)</div> :
                     SENT_WASH.includes(r.stage) ? (
                      <Btn size="sm" icon={Check} onClick={() => advance(r, next(r))}>Mark received from wash → pending approval</Btn>
                    ) : PENDING_WASH.includes(r.stage) ? (
                      <WashReview roll={r} onApprove={() => washApprove(r)} onReject={(rk) => washReject(r, rk)} />
                    ) : r.stage === "done" ? (
                      <Badge c={C.green} bg={C.greenBg}>✓ Production complete</Badge>
                    ) : r.stage === "cutting" ? (
                      <div className="text-xs" style={{ color: C.sub }}>Enter cutting quantities above, then save to move to Stitching.</div>
                    ) : (
                      <Btn size="sm" icon={ChevronRight} onClick={() => next(r) === "done" ? completeProd(r) : advance(r, next(r))}>
                        {next(r) === "done" ? "Complete → move to Finished Stock" : `Advance → ${STAGES[SIDX[next(r)]].label}`}
                      </Btn>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// wash reference pictures: upload from device (in-memory preview) or paste an image URL
function RefPics({ roll, canEdit, onAdd, onRemove }) {
  const pics = roll.washRefs || [];
  const [url, setUrl] = useState("");
  const onFile = (e) => {
    const f = e.target.files && e.target.files[0]; if (!f) return;
    const rd = new FileReader(); rd.onload = () => onAdd(rd.result); rd.readAsDataURL(f); e.target.value = "";
  };
  return (
    <div className="rounded-lg p-3" style={{ background: C.bg }}>
      <div className="text-xs font-semibold mb-2" style={{ color: C.sub }}>Wash reference pictures</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {pics.map((p, i) => (
          <div key={i} className="relative">
            <img src={p} alt="wash ref" className="h-16 w-16 rounded-lg object-cover" style={{ border: `1px solid ${C.line}` }} />
            {canEdit && <button onClick={() => onRemove(i)} className="absolute rounded-full p-0.5" style={{ top: -6, right: -6, background: C.red, color: "#fff" }}><X size={11} /></button>}
          </div>
        ))}
        {pics.length === 0 && <span className="text-xs" style={{ color: C.sub }}>No pictures yet.</span>}
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="cursor-pointer rounded-lg px-2.5 py-1 text-xs font-semibold" style={{ background: C.primaryBg, color: C.primary }}>
            + Upload<input type="file" accept="image/*" className="hidden" onChange={onFile} />
          </label>
          <input className="rounded-lg px-2 py-1 text-xs" style={{ border: `1px solid ${C.line}`, width: 170, ...body }} value={url} onChange={e => setUrl(e.target.value)} placeholder="or paste image URL" />
          <button onClick={() => { if (url.trim()) { onAdd(url.trim()); setUrl(""); } }} className="text-xs font-semibold" style={{ color: C.primary }}>Add</button>
        </div>
      )}
    </div>
  );
}

// wash review with a required remark on rejection
function WashReview({ roll, onApprove, onReject }) {
  const [remark, setRemark] = useState("");
  return (
    <div className="rounded-lg p-3" style={{ background: "#fff", border: `1px solid ${C.red}` }}>
      <div className="text-xs font-bold uppercase mb-1.5" style={{ color: C.red }}>Received from wash — your decision</div>
      <textarea value={remark} onChange={e => setRemark(e.target.value)} rows={2} placeholder="Washing remark (required if rejecting)…"
        className="w-full rounded-lg px-2 py-1.5 text-sm mb-2" style={{ border: `1px solid ${C.line}`, ...body }} />
      <div className="flex flex-wrap gap-2">
        <Btn size="sm" kind="success" icon={Check} onClick={onApprove}>Approve</Btn>
        <Btn size="sm" kind="danger" icon={X} onClick={() => onReject(remark)}>Reject — send back to wash</Btn>
      </div>
    </div>
  );
}

// overdue delay: admin queries line manager → line manager responds → admin approves
function DelayBlock({ roll, session, onUpdate }) {
  const d = roll.delay || { status: "none" };
  const isAdmin = session.role === "admin", isLine = session.role === "line_manager";
  const [adminNote, setAdminNote] = useState("");
  const [lineNote, setLineNote] = useState("");
  const setD = (patch) => onUpdate(roll.id, { delay: { ...d, ...patch } });
  const taSty = { border: `1px solid ${C.line}`, ...body };
  return (
    <div className="rounded-lg p-3" style={{ background: C.redBg }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <AlertTriangle size={14} style={{ color: C.red }} />
        <span className="text-xs font-bold uppercase" style={{ color: C.red }}>
          Overdue — {cutDays(roll)} days since cutting, not finished{d.status === "approved" ? " · delay approved" : ""}
        </span>
      </div>
      {d.status === "approved" ? (
        <div className="text-xs" style={{ color: C.sub }}>Delay approved by admin. Line manager's reason: <b>{d.lineNote || "—"}</b></div>
      ) : isAdmin ? (
        d.status === "responded" ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs">Line manager's reason: <b>{d.lineNote}</b></div>
            <Btn size="sm" kind="success" icon={Check} onClick={() => setD({ status: "approved", approvedDate: new Date().toISOString() })}>Approve delay</Btn>
          </div>
        ) : d.status === "raised" ? (
          <div className="text-xs" style={{ color: C.sub }}>Query sent to line manager — waiting for their input…</div>
        ) : (
          <div className="flex flex-col gap-2">
            <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)} rows={2} placeholder="Quote the delay to the line manager — what's holding it up?"
              className="w-full rounded-lg px-2 py-1.5 text-sm" style={taSty} />
            <Btn size="sm" kind="danger" onClick={() => setD({ status: "raised", adminNote, raisedDate: new Date().toISOString() })}>Query delay → line manager</Btn>
          </div>
        )
      ) : isLine ? (
        d.status === "raised" ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs">Admin asks: <b>{d.adminNote || "(no note)"}</b></div>
            <textarea value={lineNote} onChange={e => setLineNote(e.target.value)} rows={2} placeholder="Your reason for the delay"
              className="w-full rounded-lg px-2 py-1.5 text-sm" style={taSty} />
            <Btn size="sm" onClick={() => setD({ status: "responded", lineNote })}>Submit reason to admin</Btn>
          </div>
        ) : d.status === "responded" ? (
          <div className="text-xs" style={{ color: C.sub }}>Reason submitted — awaiting admin approval.</div>
        ) : (
          <div className="text-xs" style={{ color: C.sub }}>⚠ Overdue by {cutDays(roll)} days — awaiting admin review.</div>
        )
      ) : (
        <div className="text-xs" style={{ color: C.sub }}>{d.status === "raised" ? "Delay raised — line manager to respond." : d.status === "responded" ? "Awaiting admin approval." : "Overdue."}</div>
      )}
    </div>
  );
}

// brand selector card — shows brand name with its article list in brackets
function BrandCard({ label, sub, count, alert, active, onClick }) {
  return (
    <button onClick={onClick} className="rounded-xl px-3 py-2 text-left transition" style={{ minWidth: 140, maxWidth: 240,
      border: active ? `2px solid ${C.primary}` : `1px solid ${C.line}`, background: active ? C.primaryBg : "#fff" }}>
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-bold" style={{ ...display, color: active ? C.primary : C.ink }}>{label}</span>
        {count != null && <span className="rounded-full px-1.5 text-xs font-bold" style={{ background: active ? C.primary : C.bg, color: active ? "#fff" : C.sub }}>{count}</span>}
        {alert > 0 && <span className="rounded-full px-1.5 text-xs font-bold" style={{ background: C.redBg, color: C.red }}>⏳{alert}</span>}
      </div>
      <div className="text-xs truncate" style={{ color: C.sub }}>{sub}</div>
    </button>
  );
}

// editable detail field (uncontrolled: keeps focus while typing, saves on blur)
function DField({ label, value, onSave, options, placeholder, disabled }) {
  const opts = options && options.map(o => typeof o === "string" ? { value: o, label: o } : o);
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold" style={{ color: C.sub }}>
      {label}
      {opts ? (
        <select disabled={disabled} className={inputCls} style={inputSty} defaultValue={value || ""} onChange={e => onSave(e.target.value)}>
          {!value && <option value="">— select —</option>}
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input disabled={disabled} className={inputCls} style={inputSty} defaultValue={value || ""} placeholder={placeholder} onBlur={e => onSave(e.target.value)} />
      )}
    </label>
  );
}

// per-size cutting entry for a lot (unlocks after approval)
function CutEntry({ roll, onSave, canEdit }) {
  const [cut, setCut] = useState(() => ({ ...(roll.cutting || {}) }));
  const total = cutTotal(cut);
  return (
    <div className="rounded-lg p-3" style={{ background: C.blueBg }}>
      <div className="text-xs font-semibold uppercase mb-1.5" style={{ color: C.blue }}>Pieces cut in lot (per size)</div>
      <div className="grid grid-cols-6 gap-2">
        {PSIZES.map(s => (
          <div key={s} className="flex flex-col items-center gap-1">
            <span className="text-xs font-semibold" style={{ color: C.sub }}>{s}</span>
            <input type="number" min="0" disabled={!canEdit} defaultValue={cut[s] || ""} placeholder="0"
              onChange={e => setCut(c => ({ ...c, [s]: +e.target.value || 0 }))}
              className="w-full rounded-lg px-1 py-1.5 text-sm text-center" style={{ border: `1px solid ${C.line}`, background: "#fff", ...mono }} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-sm font-bold" style={mono}>Total: <span style={{ color: C.blue }}>{total} pcs</span></span>
        {canEdit && roll.stage === "cutting" && <Btn size="sm" icon={Check} onClick={() => onSave(cut)}>Save cutting → Stitching</Btn>}
        {canEdit && roll.stage !== "cutting" && <Btn size="sm" kind="ghost" icon={Check} onClick={() => onSave(cut)}>Update cutting</Btn>}
      </div>
    </div>
  );
}

/* ============================ INVENTORY ============================ */
function Inventory({ raw, createRaw, finished, createFinished, updateFinished, rejects, createReject, updateReject, sales, createSale, drill, session }) {
  const [tab, setTab] = useState("raw");
  const canEdit = session.role === "admin" || session.role === "plant_manager";
  const SIZES = ["28", "30", "32", "34", "36", "38"];
  const tabs = [["raw", "Raw Material"], ["fin", "Finished Stock"], ["rej", "Rejected Pieces"], ["sales", "Sales"]];

  // forms
  const [rf, setRf] = useState({ name: "", cat: "fabric", supplier: "", rate: "", qty: "", unit: "m" });
  const RAW_CATS = [["fabric", "Fabric", C.primary, C.primaryBg], ["trim", "Trim", C.blue, C.blueBg], ["chemical", "Chemical/Dye", C.amber, C.amberBg], ["other", "Other", C.sub, C.bg]];
  const catTag = (c) => RAW_CATS.find(x => x[0] === c) || RAW_CATS[3];
  const [ff, setFf] = useState({ article: "", colour: "", s: {} });
  const [jf, setJf] = useState({ article: "", colour: "", size: "32", type: "minor", qty: "", note: "" });

  return (
    <div>
      <PageHead title="Inventory" sub="Raw material, finished stock & rejections — plant manager"
        right={<Btn kind="ghost" icon={Download} onClick={() => {
          if (tab === "raw") downloadCSV("raw-material.csv", [["Material","Category","Supplier","Rate","Qty","Unit","Status"], ...raw.map(r => [r.name, r.cat, r.supplier, r.rate, r.qty, r.unit, r.qty < 100 ? "LOW" : "OK"])]);
          if (tab === "rej") downloadCSV("rejections.csv", [["Article","Colour","Size","Type","Qty","Date","Note"], ...rejects.map(r => [r.article, r.colour, r.size, r.type, r.qty, r.date, r.note])]);
          if (tab === "fin") downloadCSV("finished-stock.csv", [["Article","Colour",...SIZES,"Total"], ...finished.map(f => [f.article, f.colour, ...SIZES.map(s => f.s[s]||0), SIZES.reduce((a,s)=>a+(f.s[s]||0),0)])]);
        }}>Export CSV</Btn>} />

      <div className="mb-4 inline-flex rounded-lg p-1" style={{ background: "#fff", border: `1px solid ${C.line}` }}>
        {tabs.map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} className="rounded-md px-3 py-1.5 text-sm font-semibold"
            style={tab === id ? { background: C.primary, color: "#fff" } : { color: C.sub }}>{l}</button>
        ))}
      </div>

      {tab === "raw" && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr style={{ background: C.bg, color: C.sub }} className="text-left text-xs font-semibold uppercase">
              <th className="px-4 py-2.5">Material</th><th className="px-4 py-2.5">Category</th><th className="px-4 py-2.5">Supplier</th>
              <th className="px-4 py-2.5">Rate</th><th className="px-4 py-2.5">Quantity</th><th className="px-4 py-2.5"></th>
            </tr></thead>
            <tbody>
              {raw.map(r => {
                const lowFlag = r.qty < 100;
                return (
                  <tr key={r.id} onClick={() => drill({ title: r.name, tag: lowFlag ? { c: C.red, bg: C.redBg, t: "LOW STOCK" } : { c: C.green, bg: C.greenBg, t: "IN STOCK" },
                    fields: [["Category", catTag(r.cat)[1]], ["Supplier", r.supplier], ["Rate", inr(r.rate) + " / " + r.unit], ["Quantity", `${r.qty} ${r.unit}`], ["Stock value", inr(r.rate * r.qty)], ["Threshold", "100 " + r.unit]] })}
                    className="cursor-pointer" style={{ borderTop: `1px solid ${C.line}`, background: lowFlag ? C.redBg : "#fff" }}>
                    <td className="px-4 py-2.5 font-semibold" style={lowFlag ? { color: C.red } : {}}>
                      {lowFlag && <AlertTriangle size={13} className="inline mr-1" />}{r.name}</td>
                    <td className="px-4 py-2.5"><Badge c={catTag(r.cat)[2]} bg={catTag(r.cat)[3]}>{catTag(r.cat)[1]}</Badge></td>
                    <td className="px-4 py-2.5" style={{ color: C.sub }}>{r.supplier}</td>
                    <td className="px-4 py-2.5" style={mono}>{inr(r.rate)}<span style={{ color: C.sub }}>/{r.unit}</span></td>
                    <td className="px-4 py-2.5 font-semibold" style={{ ...mono, color: lowFlag ? C.red : C.ink }}>{r.qty} {r.unit}</td>
                    <td className="px-4 py-2.5">{lowFlag && <Badge c={C.red} bg="#fff">REORDER</Badge>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {canEdit && (
            <div className="flex flex-wrap items-end gap-2 p-3" style={{ borderTop: `1px solid ${C.line}`, background: C.bg }}>
              <Field label="Material"><input className={inputCls} style={{...inputSty, width:160}} value={rf.name} onChange={e=>setRf({...rf,name:e.target.value})} /></Field>
              <Field label="Category"><select className={inputCls} style={{...inputSty, width:120}} value={rf.cat} onChange={e=>setRf({...rf,cat:e.target.value})}>{RAW_CATS.map(c=><option key={c[0]} value={c[0]}>{c[1]}</option>)}</select></Field>
              <Field label="Supplier"><input className={inputCls} style={{...inputSty, width:130}} value={rf.supplier} onChange={e=>setRf({...rf,supplier:e.target.value})} /></Field>
              <Field label="Rate ₹"><input type="number" className={inputCls} style={{...inputSty, width:80}} value={rf.rate} onChange={e=>setRf({...rf,rate:e.target.value})} /></Field>
              <Field label="Qty"><input type="number" className={inputCls} style={{...inputSty, width:80}} value={rf.qty} onChange={e=>setRf({...rf,qty:e.target.value})} /></Field>
              <Field label="Unit"><input className={inputCls} style={{...inputSty, width:60}} value={rf.unit} onChange={e=>setRf({...rf,unit:e.target.value})} /></Field>
              <Btn icon={Plus} onClick={() => { if(!rf.name)return; createRaw({...rf,rate:+rf.rate||0,qty:+rf.qty||0}); setRf({name:"",cat:"fabric",supplier:"",rate:"",qty:"",unit:"m"}); }}>Add</Btn>
            </div>
          )}
        </Card>
      )}

      {tab === "fin" && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr style={{ background: C.bg, color: C.sub }} className="text-left text-xs font-semibold uppercase">
              <th className="px-4 py-2.5">Article #</th><th className="px-4 py-2.5">Colour</th>
              {SIZES.map(s => <th key={s} className="px-2 py-2.5 text-center">{s}</th>)}<th className="px-4 py-2.5 text-center">Total</th>
            </tr></thead>
            <tbody>
              {finished.map(f => {
                const tot = SIZES.reduce((a, s) => a + (f.s[s] || 0), 0);
                return (
                  <tr key={f.id} onClick={() => drill({ title: f.article, tag: { c: C.blue, bg: C.blueBg, t: "FINISHED STOCK" },
                    fields: [["Colour", f.colour], ...SIZES.map(s => [`Size ${s}`, (f.s[s]||0) + " pcs"]), ["Total", tot + " pcs"]] })}
                    className="cursor-pointer" style={{ borderTop: `1px solid ${C.line}` }}>
                    <td className="px-4 py-2.5 font-semibold">{f.article}</td>
                    <td className="px-4 py-2.5" style={{ color: C.sub }}>{f.colour}</td>
                    {SIZES.map(s => <td key={s} className="px-2 py-2.5 text-center" style={mono}>{f.s[s] || "—"}</td>)}
                    <td className="px-4 py-2.5 text-center font-bold" style={{ ...mono, color: C.blue }}>{tot}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {canEdit && (
            <div className="flex flex-wrap items-end gap-2 p-3" style={{ borderTop: `1px solid ${C.line}`, background: C.bg }}>
              <Field label="Article #"><input className={inputCls} style={{...inputSty,width:110}} value={ff.article} onChange={e=>setFf({...ff,article:e.target.value})} /></Field>
              <Field label="Colour"><input className={inputCls} style={{...inputSty,width:120}} value={ff.colour} onChange={e=>setFf({...ff,colour:e.target.value})} /></Field>
              {SIZES.map(s => <Field key={s} label={s}><input type="number" className={inputCls} style={{...inputSty,width:52}} value={ff.s[s]||""} onChange={e=>setFf({...ff,s:{...ff.s,[s]:+e.target.value||0}})} /></Field>)}
              <Btn icon={Plus} onClick={() => { if(!ff.article)return; createFinished({brand:"",article:ff.article,colour:ff.colour,s:ff.s}); setFf({article:"",colour:"",s:{}}); }}>Add</Btn>
            </div>
          )}
        </Card>
      )}

      {tab === "rej" && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr style={{ background: C.bg, color: C.sub }} className="text-left text-xs font-semibold uppercase">
              <th className="px-4 py-2.5">Article #</th><th className="px-4 py-2.5">Colour</th><th className="px-4 py-2.5">Size</th>
              <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Qty</th><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Note</th>
            </tr></thead>
            <tbody>
              {rejects.map(r => {
                const major = r.type === "major";
                return (
                  <tr key={r.id} onClick={() => drill({ title: `${r.article} — reject`, tag: major ? { c: C.red, bg: C.redBg, t: "MAJOR" } : { c: C.amber, bg: C.amberBg, t: "MINOR" },
                    fields: [["Article", r.article], ["Colour", r.colour], ["Size", r.size], ["Type", r.type], ["Qty", r.qty + " pcs"], ["Date", fmtDate(r.date)], ["Note", r.note || "—"]] })}
                    className="cursor-pointer" style={{ borderTop: `1px solid ${C.line}` }}>
                    <td className="px-4 py-2.5 font-semibold">{r.article}</td>
                    <td className="px-4 py-2.5" style={{ color: C.sub }}>{r.colour}</td>
                    <td className="px-4 py-2.5" style={mono}>{r.size}</td>
                    <td className="px-4 py-2.5"><Badge c={major ? C.red : C.amber} bg={major ? C.redBg : C.amberBg}>{r.type}</Badge></td>
                    <td className="px-4 py-2.5 font-semibold" style={mono}>{r.qty}</td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: C.sub }}>{fmtDate(r.date)}</td>
                    <td className="px-4 py-2.5 text-xs" style={{ color: C.sub }}>{r.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {canEdit && (
            <div className="flex flex-wrap items-end gap-2 p-3" style={{ borderTop: `1px solid ${C.line}`, background: C.bg }}>
              <Field label="Article #"><input className={inputCls} style={{...inputSty,width:100}} value={jf.article} onChange={e=>setJf({...jf,article:e.target.value})} /></Field>
              <Field label="Colour"><input className={inputCls} style={{...inputSty,width:110}} value={jf.colour} onChange={e=>setJf({...jf,colour:e.target.value})} /></Field>
              <Field label="Size"><select className={inputCls} style={{...inputSty,width:70}} value={jf.size} onChange={e=>setJf({...jf,size:e.target.value})}>{SIZES.map(s=><option key={s}>{s}</option>)}</select></Field>
              <Field label="Type"><select className={inputCls} style={{...inputSty,width:90}} value={jf.type} onChange={e=>setJf({...jf,type:e.target.value})}><option value="minor">minor</option><option value="major">major</option></select></Field>
              <Field label="Qty"><input type="number" className={inputCls} style={{...inputSty,width:70}} value={jf.qty} onChange={e=>setJf({...jf,qty:e.target.value})} /></Field>
              <Field label="Note"><input className={inputCls} style={{...inputSty,width:150}} value={jf.note} onChange={e=>setJf({...jf,note:e.target.value})} /></Field>
              <Btn icon={Plus} onClick={() => { if(!jf.article)return; createReject({brand:"",...jf,qty:+jf.qty||0,date:today()}); setJf({article:"",colour:"",size:"32",type:"minor",qty:"",note:""}); }}>Add</Btn>
            </div>
          )}
        </Card>
      )}

      {tab === "sales" && <SalesTab finished={finished} updateFinished={updateFinished} rejects={rejects} updateReject={updateReject} sales={sales} createSale={createSale} canEdit={canEdit} drill={drill} SIZES={SIZES} />}
    </div>
  );
}

/* ============================ SALES ============================ */
function SalesTab({ finished, updateFinished, rejects, updateReject, sales, createSale, canEdit, drill, SIZES }) {
  const [kind, setKind] = useState("fresh");
  const [ff, setFf] = useState({ id: "", size: "", qty: "", buyer: "", rate: "" });   // fresh sale form
  const [rf, setRf] = useState({ id: "", qty: "", buyer: "", rate: "" });               // rejection sale form
  const [formErr, setFormErr] = useState("");

  const selFin = finished.find(f => f.id === ff.id);
  const selRej = rejects.find(j => j.id === rf.id);

  const logFresh = () => {
    setFormErr("");
    const q = +ff.qty || 0;
    if (!selFin) return setFormErr("Select an article from finished stock.");
    if (!ff.size) return setFormErr("Select a size.");
    if (q <= 0) return setFormErr("Enter a quantity greater than 0.");
    const avail = selFin.s[ff.size] || 0;
    if (q > avail) return setFormErr(`Only ${avail} pcs of size ${ff.size} in stock — cannot sell ${q}.`);
    updateFinished(selFin.id, { s: { ...selFin.s, [ff.size]: avail - q } });
    createSale({ kind: "fresh", srcId: selFin.id, brand: selFin.brand || "—", article: selFin.article, colour: selFin.colour, size: ff.size, qty: q, buyer: ff.buyer, rate: +ff.rate || 0, date: today(), status: "pending" });
    setFf({ id: "", size: "", qty: "", buyer: "", rate: "" });
  };
  const logRej = () => {
    setFormErr("");
    const q = +rf.qty || 0;
    if (!selRej) return setFormErr("Select a rejected lot.");
    if (q <= 0) return setFormErr("Enter a quantity greater than 0.");
    if (q > selRej.qty) return setFormErr(`Only ${selRej.qty} pcs available in this rejected lot — cannot sell ${q}.`);
    updateReject(selRej.id, { qty: selRej.qty - q });
    createSale({ kind: "rejection", srcId: selRej.id, brand: selRej.brand || "—", article: selRej.article, colour: selRej.colour, size: selRej.size, qty: q, buyer: rf.buyer, rate: +rf.rate || 0, date: today(), status: "pending" });
    setRf({ id: "", qty: "", buyer: "", rate: "" });
  };

  // brand-wise summary
  const byBrand = {};
  sales.forEach(s => {
    byBrand[s.brand] = byBrand[s.brand] || { fresh: 0, rejection: 0 };
    byBrand[s.brand][s.kind] += s.qty;
  });

  const stTag = (s) => s === "approved" ? { c: C.green, bg: C.greenBg, t: "Approved" } : { c: C.amber, bg: C.amberBg, t: "Pending approval" };

  return (
    <div className="flex flex-col gap-4">
      {/* BRAND-WISE SUMMARY */}
      <Card className="p-4">
        <h3 className="font-bold mb-2" style={display}>Sold — brand-wise summary</h3>
        {Object.keys(byBrand).length === 0 ? <Empty small label="No sales logged yet" /> : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {Object.entries(byBrand).map(([b, v]) => (
              <div key={b} className="rounded-lg p-3" style={{ background: C.bg }}>
                <div className="text-sm font-bold" style={{ ...display, color: C.primary }}>{b}</div>
                <div className="text-xs mt-1" style={{ color: C.sub }}>Fresh <b style={{ color: C.green }}>{v.fresh}</b> · Rejection <b style={{ color: C.red }}>{v.rejection}</b></div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {canEdit && (
        <Card className="p-4">
          <div className="mb-3 inline-flex rounded-lg p-1" style={{ background: C.bg }}>
            {[["fresh", "Fresh"], ["rejection", "Rejections"]].map(([k, l]) => (
              <button key={k} onClick={() => setKind(k)} className="rounded-md px-4 py-1.5 text-sm font-semibold"
                style={kind === k ? { background: "#fff", color: k === "fresh" ? C.green : C.red, border: `1px solid ${C.line}` } : { color: C.sub }}>{l}</button>
            ))}
          </div>

          {kind === "fresh" ? (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Article (finished stock)">
                <select className={inputCls} style={{ ...inputSty, width: 200 }} value={ff.id} onChange={e => setFf({ ...ff, id: e.target.value, size: "" })}>
                  <option value="">— select article —</option>
                  {finished.map(f => <option key={f.id} value={f.id}>{f.brand} · {f.article} ({f.colour})</option>)}
                </select>
              </Field>
              <Field label="Size">
                <select className={inputCls} style={{ ...inputSty, width: 110 }} value={ff.size} onChange={e => setFf({ ...ff, size: e.target.value })} disabled={!selFin}>
                  <option value="">— size —</option>
                  {selFin && SIZES.filter(s => (selFin.s[s] || 0) > 0).map(s => <option key={s} value={s}>{s} (avail {selFin.s[s]})</option>)}
                </select>
              </Field>
              <Field label="Qty"><input type="number" className={inputCls} style={{ ...inputSty, width: 80 }} value={ff.qty} onChange={e => setFf({ ...ff, qty: e.target.value })} /></Field>
              <Field label="Buyer"><input className={inputCls} style={{ ...inputSty, width: 150 }} value={ff.buyer} onChange={e => setFf({ ...ff, buyer: e.target.value })} /></Field>
              <Field label="Rate ₹"><input type="number" className={inputCls} style={{ ...inputSty, width: 90 }} value={ff.rate} onChange={e => setFf({ ...ff, rate: e.target.value })} /></Field>
              <Btn icon={Plus} onClick={logFresh}>Log sale</Btn>
              {selFin && <div className="w-full text-xs mt-1" style={{ color: C.sub }}>In stock — {SIZES.map(s => `${s}:${selFin.s[s] || 0}`).join("  ")}</div>}
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="Rejected piece">
                <select className={inputCls} style={{ ...inputSty, width: 280 }} value={rf.id} onChange={e => setRf({ ...rf, id: e.target.value })}>
                  <option value="">— select rejected lot —</option>
                  {rejects.filter(j => j.qty > 0).map(j => <option key={j.id} value={j.id}>{j.brand} · {j.article} · sz {j.size} · {j.type} (avail {j.qty})</option>)}
                </select>
              </Field>
              <Field label="Qty"><input type="number" className={inputCls} style={{ ...inputSty, width: 80 }} value={rf.qty} onChange={e => setRf({ ...rf, qty: e.target.value })} /></Field>
              <Field label="Buyer"><input className={inputCls} style={{ ...inputSty, width: 150 }} value={rf.buyer} onChange={e => setRf({ ...rf, buyer: e.target.value })} /></Field>
              <Field label="Rate ₹"><input type="number" className={inputCls} style={{ ...inputSty, width: 90 }} value={rf.rate} onChange={e => setRf({ ...rf, rate: e.target.value })} /></Field>
              <Btn icon={Plus} onClick={logRej}>Log sale</Btn>
            </div>
          )}
          {formErr && <div className="mt-2 rounded-lg px-3 py-1.5 text-xs font-semibold" style={{ background: C.redBg, color: C.red }}>⚠ {formErr}</div>}
          <p className="mt-2 text-xs" style={{ color: C.sub }}>Logging a sale deducts the goods from stock immediately. It then waits for admin approval before posting to dispatch history.</p>
        </Card>
      )}

      {/* SALES LOG */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2.5 font-bold" style={{ ...display, borderBottom: `1px solid ${C.line}` }}>Sales log</div>
        <table className="w-full text-sm">
          <thead><tr style={{ background: C.bg, color: C.sub }} className="text-left text-xs font-semibold uppercase">
            <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Article</th><th className="px-4 py-2.5">Brand</th>
            <th className="px-4 py-2.5">Size</th><th className="px-4 py-2.5">Qty</th><th className="px-4 py-2.5">Buyer</th><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Status</th>
          </tr></thead>
          <tbody>
            {sales.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-sm" style={{ color: C.sub }}>No sales yet.</td></tr>}
            {sales.map(s => (
              <tr key={s.id} className="cursor-pointer" style={{ borderTop: `1px solid ${C.line}` }}
                onClick={() => drill({ title: `${s.article} — ${s.kind}`, tag: stTag(s.status),
                  fields: [["Brand", s.brand], ["Colour", s.colour], ["Size", s.size], ["Qty", s.qty + " pcs"], ["Buyer", s.buyer || "—"], ["Rate", inr(s.rate)], ["Value", inr(s.rate * s.qty)], ["Date", fmtDate(s.date)], ["Status", stTag(s.status).t], ...(s.dispatchedAt ? [["Dispatched", fmtDate(s.dispatchedAt)]] : [])] })}>
                <td className="px-4 py-2.5"><Badge c={s.kind === "fresh" ? C.green : C.red} bg={s.kind === "fresh" ? C.greenBg : C.redBg}>{s.kind}</Badge></td>
                <td className="px-4 py-2.5 font-semibold">{s.article}</td>
                <td className="px-4 py-2.5" style={{ color: C.sub }}>{s.brand}</td>
                <td className="px-4 py-2.5" style={mono}>{s.size}</td>
                <td className="px-4 py-2.5 font-semibold" style={mono}>{s.qty}</td>
                <td className="px-4 py-2.5 text-xs">{s.buyer || "—"}</td>
                <td className="px-4 py-2.5 text-xs" style={{ color: C.sub }}>{fmtDate(s.date)}</td>
                <td className="px-4 py-2.5"><Badge {...stTag(s.status)}>{stTag(s.status).t}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ============================ LOGISTICS ============================ */
function Logistics({ couriers, createCourier, updateCourier, sales, drill, session }) {
  const canEdit = session.role === "admin" || session.role === "plant_manager";
  const [filter, setFilter] = useState("all");
  const [f, setF] = useState({ dir: "in", company: "", dispatch: today(), eta: "", particulars: "" });
  const list = couriers.filter(c => filter === "all" || c.dir === filter);
  const stTag = (s) => { const m = COURIER_STATUS.find(x => x[0] === s) || COURIER_STATUS[1]; return { c: m[2], bg: m[3], t: m[1] }; };
  const dispatched = (sales || []).filter(s => s.status === "approved");
  const setStatus = (id, status) => updateCourier(id, { status });

  return (
    <div>
      <PageHead title="Logistics" sub="Incoming & outgoing couriers — updated by plant manager"
        right={<div className="inline-flex rounded-lg p-1" style={{ background: "#fff", border: `1px solid ${C.line}` }}>
          {[["all","All"],["in","Incoming"],["out","Outgoing"]].map(([id,l]) => (
            <button key={id} onClick={() => setFilter(id)} className="rounded-md px-3 py-1.5 text-xs font-semibold"
              style={filter===id?{background:C.primary,color:"#fff"}:{color:C.sub}}>{l}</button>))}
        </div>} />
      <Card className="overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead><tr style={{ background: C.bg, color: C.sub }} className="text-left text-xs font-semibold uppercase">
            <th className="px-4 py-2.5">Dir</th><th className="px-4 py-2.5">Company</th><th className="px-4 py-2.5">Dispatched</th>
            <th className="px-4 py-2.5">Expected</th><th className="px-4 py-2.5">Particulars</th><th className="px-4 py-2.5">Present status</th>
          </tr></thead>
          <tbody>
            {list.map(c => (
              <tr key={c.id} style={{ borderTop: `1px solid ${C.line}` }}>
                <td className="px-4 py-2.5"><Badge {...dirTag(c.dir)}>{c.dir === "in" ? "IN" : "OUT"}</Badge></td>
                <td className="px-4 py-2.5 font-semibold cursor-pointer" onClick={() => drill({ title: c.company, tag: dirTag(c.dir),
                  fields: [["Direction", c.dir === "in" ? "Incoming" : "Outgoing"], ["Dispatched", fmtDate(c.dispatch)], ["Expected delivery", fmtDate(c.eta)], ["Particulars", c.particulars], ["Present status", stTag(c.status).t]] })}>{c.company}</td>
                <td className="px-4 py-2.5 text-xs" style={{ color: C.sub }}>{fmtDate(c.dispatch)}</td>
                <td className="px-4 py-2.5 text-xs" style={{ color: C.sub }}>{fmtDate(c.eta)}</td>
                <td className="px-4 py-2.5 text-xs">{c.particulars}</td>
                <td className="px-4 py-2.5">
                  {canEdit ? (
                    <select value={c.status} onChange={e => setStatus(c.id, e.target.value)} className="rounded-lg px-2 py-1 text-xs font-semibold"
                      style={{ border: `1px solid ${stTag(c.status).c}`, color: stTag(c.status).c, background: stTag(c.status).bg, ...body }}>
                      {COURIER_STATUS.map(s => <option key={s[0]} value={s[0]}>{s[1]}</option>)}
                    </select>
                  ) : <Badge {...stTag(c.status)}>{stTag(c.status).t}</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEdit && (
          <div className="flex flex-wrap items-end gap-2 p-3" style={{ borderTop: `1px solid ${C.line}`, background: C.bg }}>
            <Field label="Direction"><select className={inputCls} style={{...inputSty,width:100}} value={f.dir} onChange={e=>setF({...f,dir:e.target.value})}><option value="in">Incoming</option><option value="out">Outgoing</option></select></Field>
            <Field label="Company"><input className={inputCls} style={{...inputSty,width:130}} value={f.company} onChange={e=>setF({...f,company:e.target.value})} /></Field>
            <Field label="Dispatch"><input type="date" className={inputCls} style={{...inputSty,width:140}} value={f.dispatch} onChange={e=>setF({...f,dispatch:e.target.value})} /></Field>
            <Field label="Expected"><input type="date" className={inputCls} style={{...inputSty,width:140}} value={f.eta} onChange={e=>setF({...f,eta:e.target.value})} /></Field>
            <Field label="Particulars"><input className={inputCls} style={{...inputSty,width:180}} value={f.particulars} onChange={e=>setF({...f,particulars:e.target.value})} /></Field>
            <Btn icon={Plus} onClick={() => { if(!f.company)return; createCourier({...f,status:"pickup"}); setF({dir:"in",company:"",dispatch:today(),eta:"",particulars:""}); }}>Add courier</Btn>
          </div>
        )}
      </Card>

      {/* DISPATCHED ARTICLES HISTORY — from admin-approved sales */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2.5 font-bold" style={{ ...display, borderBottom: `1px solid ${C.line}` }}>Dispatched Articles History</div>
        <table className="w-full text-sm">
          <thead><tr style={{ background: C.bg, color: C.sub }} className="text-left text-xs font-semibold uppercase">
            <th className="px-4 py-2.5">Dispatched</th><th className="px-4 py-2.5">Article</th><th className="px-4 py-2.5">Brand</th>
            <th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Size</th><th className="px-4 py-2.5">Qty</th><th className="px-4 py-2.5">Buyer</th>
          </tr></thead>
          <tbody>
            {dispatched.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-sm" style={{ color: C.sub }}>Nothing dispatched yet — approve sales from the dashboard.</td></tr>}
            {dispatched.map(s => (
              <tr key={s.id} style={{ borderTop: `1px solid ${C.line}` }}>
                <td className="px-4 py-2.5 text-xs" style={{ color: C.sub }}>{fmtDate(s.dispatchedAt || s.date)}</td>
                <td className="px-4 py-2.5 font-semibold">{s.article}</td>
                <td className="px-4 py-2.5" style={{ color: C.sub }}>{s.brand}</td>
                <td className="px-4 py-2.5"><Badge c={s.kind === "fresh" ? C.green : C.red} bg={s.kind === "fresh" ? C.greenBg : C.redBg}>{s.kind}</Badge></td>
                <td className="px-4 py-2.5" style={mono}>{s.size}</td>
                <td className="px-4 py-2.5 font-semibold" style={mono}>{s.qty}</td>
                <td className="px-4 py-2.5 text-xs">{s.buyer || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ============================ ATTENDANCE ============================ */
function Attendance({ staff, createStaff, attendance, markAttendance, session, drill }) {
  const canEdit = session.role === "admin" || session.role === "attendance";
  const isAdmin = session.role === "admin";
  const [view, setView] = useState("mark");        // mark | history
  const [date, setDate] = useState(today());
  const [adding, setAdding] = useState(false);
  const [ef, setEf] = useState({ name: "", role: "" });
  const day = attendance[date] || {};
  const set = (sid, v) => markAttendance(date, sid, v);
  const opts = [["P", "Present", C.green, C.greenBg], ["H", "Half", C.amber, C.amberBg], ["A", "Absent", C.red, C.redBg]];
  const counts = { P: 0, H: 0, A: 0 }; staff.forEach(s => { if (day[s.id]) counts[day[s.id]]++; });
  const halfLocked = (sid) => day[sid] === "H" && !isAdmin;
  const histDates = Object.keys(attendance).sort().reverse();
  const cell = (v) => v === "P" ? { t: "P", c: C.green } : v === "H" ? { t: "H", c: C.amber } : v === "A" ? { t: "A", c: C.red } : { t: "·", c: C.line };
  const tally = (sid) => { const t = { P: 0, H: 0, A: 0 }; histDates.forEach(d => { const v = attendance[d]?.[sid]; if (v) t[v]++; }); return t; };
  const addEmp = () => { if (!ef.name.trim()) return; createStaff({ name: ef.name.trim(), role: ef.role.trim() || "Staff" }); setEf({ name: "", role: "" }); setAdding(false); };

  return (
    <div>
      <PageHead title="Attendance" sub="Permanent staff — marked by the attendance officer"
        right={<div className="flex gap-2">
          <div className="inline-flex rounded-lg p-1" style={{ background: "#fff", border: `1px solid ${C.line}` }}>
            {[["mark", "Mark"], ["history", "History"]].map(([id, l]) => (
              <button key={id} onClick={() => setView(id)} className="rounded-md px-3 py-1.5 text-xs font-semibold"
                style={view === id ? { background: C.primary, color: "#fff" } : { color: C.sub }}>{l}</button>))}
          </div>
          {view === "mark" && <input type="date" className={inputCls} style={inputSty} value={date} onChange={e => setDate(e.target.value)} />}
          {canEdit && <Btn icon={Plus} onClick={() => setAdding(a => !a)}>New employee</Btn>}
        </div>} />

      {adding && canEdit && (
        <Card className="mb-4 p-4">
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Employee name"><input className={inputCls} style={{ ...inputSty, width: 180 }} value={ef.name} onChange={e => setEf({ ...ef, name: e.target.value })} placeholder="Full name" /></Field>
            <Field label="Role / department"><input className={inputCls} style={{ ...inputSty, width: 180 }} value={ef.role} onChange={e => setEf({ ...ef, role: e.target.value })} placeholder="e.g. Tailor (Line B)" /></Field>
            <Btn icon={Check} onClick={addEmp}>Add employee</Btn>
          </div>
        </Card>
      )}

      {view === "history" ? (
        <Card className="overflow-x-auto">
          <div className="px-4 py-2.5 font-bold" style={{ ...display, borderBottom: `1px solid ${C.line}` }}>Attendance history</div>
          {histDates.length === 0 ? <Empty small label="No history yet" /> : (
            <table className="w-full text-sm">
              <thead><tr style={{ background: C.bg, color: C.sub }} className="text-left text-xs font-semibold uppercase">
                <th className="px-4 py-2.5">Employee</th>
                {histDates.map(d => <th key={d} className="px-2 py-2.5 text-center">{new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</th>)}
                <th className="px-3 py-2.5 text-center">P/H/A</th>
              </tr></thead>
              <tbody>
                {staff.map(s => { const t = tally(s.id); return (
                  <tr key={s.id} style={{ borderTop: `1px solid ${C.line}` }}>
                    <td className="px-4 py-2.5"><div className="font-semibold">{s.name}</div><div className="text-xs" style={{ color: C.sub }}>{s.role}</div></td>
                    {histDates.map(d => { const cl = cell(attendance[d]?.[s.id]); return <td key={d} className="px-2 py-2.5 text-center font-bold" style={{ ...mono, color: cl.c }}>{cl.t}</td>; })}
                    <td className="px-3 py-2.5 text-center text-xs font-semibold" style={mono}><span style={{ color: C.green }}>{t.P}</span>/<span style={{ color: C.amber }}>{t.H}</span>/<span style={{ color: C.red }}>{t.A}</span></td>
                  </tr>
                ); })}
              </tbody>
            </table>
          )}
        </Card>
      ) : (
      <>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {opts.map(([k, l, c, bg]) => (
          <Card key={k} className="p-3"><div className="text-2xl font-extrabold" style={{ ...display, color: c }}>{counts[k]}</div><div className="text-xs font-semibold" style={{ color: C.sub }}>{l}</div></Card>
        ))}
      </div>
      {!isAdmin && counts.H > 0 && (
        <div className="rounded-lg px-3 py-2 mb-3 text-xs font-semibold" style={{ background: C.amberBg, color: C.amber }}>
          Half-day entries can only be changed by the admin. Contact admin if an override is needed.
        </div>
      )}
      <Card className="overflow-hidden">
        {staff.map(s => {
          const hl = halfLocked(s.id);
          return (
            <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-3" style={{ borderTop: `1px solid ${C.line}` }}>
              <button onClick={() => drill({ title: s.name, tag: { c: C.primary, bg: C.primaryBg, t: "PERMANENT STAFF" }, fields: [["Role", s.role], ["Status today", opts.find(o=>o[0]===day[s.id])?.[1] || "Not marked"], ["Date", fmtDate(date)], ...(hl ? [["Override", "Admin only"]] : [])] })}
                className="text-left">
                <div className="font-semibold">{s.name}</div>
                <div className="text-xs" style={{ color: C.sub }}>{s.role}</div>
                {hl && <div className="text-xs font-semibold mt-0.5" style={{ color: C.amber }}>🔒 Half-day — admin override only</div>}
              </button>
              <div className="flex gap-1.5">
                {opts.map(([k, l, c, bg]) => {
                  const active = day[s.id] === k;
                  const disabled = !canEdit || (hl && k !== "H");
                  return (
                    <button key={k} disabled={disabled} onClick={() => !disabled && set(s.id, k)}
                      className="rounded-lg px-3 py-1.5 text-xs font-semibold transition"
                      style={active ? { background: c, color: "#fff" } : { background: bg, color: c, opacity: disabled ? 0.35 : 1 }}>{l}</button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </Card>
      </>
      )}
    </div>
  );
}

/* ============================ WAGES ============================ */
function Wages({ labour, createLabour, weeks, createWeek, updateWeek, session, drill }) {
  const canEdit = session.role === "admin" || session.role === "wages";
  const isAdmin = session.role === "admin";
  const [wi, setWi] = useState(weeks.length - 1);
  const week = weeks[wi] || null;
  const [lf, setLf] = useState({ name: "", method: "upi", upi: "" });

  const setAmt = (lid, amt) => week && updateWeek(week.id, { entries: { ...week.entries, [lid]: amt } });
  const total = week ? labour.reduce((a, l) => a + (+week.entries[l.id] || 0), 0) : 0;

  const addWeek = () => {
    const last = weeks[weeks.length - 1];
    const d = last ? new Date(last.date) : new Date(); d.setDate(d.getDate() + (last ? 7 : 0));
    createWeek({ date: d.toISOString().slice(0, 10), type: last && last.type === "kharchi" ? "wages" : "kharchi", entries: {} });
    setWi(weeks.length);
  };
  const download = () => downloadCSV(`wages-${week.date}-${week.type}.csv`,
    [["Name", "Payment Type", "Amount (₹)", "Method", "UPI ID", "Saturday"],
     ...labour.map(l => [l.name, week.type, week.entries[l.id] || 0, l.method.toUpperCase(), l.method === "upi" ? l.upi : "—", week.date])]);

  // guard: no weeks yet — render an empty state instead of crashing
  if (!week) return (
    <div>
      <PageHead title="Contractual Wages" sub="Updated every Saturday — kharchi & wages alternate weekly"
        right={canEdit && <Btn icon={Plus} onClick={addWeek}>Add first Saturday</Btn>} />
      <Card className="p-8"><Empty label="No wage weeks yet. Add the first Saturday to begin." /></Card>
    </div>
  );

  const wTag = week.type === "kharchi" ? { c: C.amber, bg: C.amberBg, t: "KHARCHI WEEK" } : { c: C.green, bg: C.greenBg, t: "WAGES WEEK" };

  return (
    <div>
      <PageHead title="Contractual Wages" sub="Updated every Saturday — kharchi & wages alternate weekly"
        right={<div className="flex gap-2">
          <select className={inputCls} style={inputSty} value={wi} onChange={e => setWi(+e.target.value)}>
            {weeks.map((w, i) => <option key={w.id} value={i}>{fmtDate(w.date)} · {w.type}</option>)}
          </select>
          {canEdit && <Btn kind="ghost" icon={Plus} onClick={addWeek}>Next Saturday</Btn>}
          <Btn icon={Download} onClick={download}>Download sheet</Btn>
        </div>} />

      <div className="mb-3 flex items-center gap-3">
        <Badge {...wTag}>{wTag.t}</Badge>
        <span className="text-sm" style={{ color: C.sub }}>Saturday {fmtDate(week.date)}</span>
        <span className="ml-auto text-sm font-bold" style={mono}>Total payout: <span style={{ color: C.primary }}>{inr(total)}</span></span>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr style={{ background: C.bg, color: C.sub }} className="text-left text-xs font-semibold uppercase">
            <th className="px-4 py-2.5">Labourer</th><th className="px-4 py-2.5">Method</th>
            <th className="px-4 py-2.5">UPI ID</th><th className="px-4 py-2.5">Amount (₹)</th>
          </tr></thead>
          <tbody>
            {labour.map(l => (
              <tr key={l.id} style={{ borderTop: `1px solid ${C.line}` }}>
                <td className="px-4 py-2.5">
                  <button onClick={() => drill({ title: l.name, tag: wTag, fields: [["Method", l.method.toUpperCase()], ["UPI ID", l.method==="upi"?l.upi:"—"], ["This Saturday", week.type], ["Amount", inr(week.entries[l.id]||0)]] })}
                    className="font-semibold text-left">{l.name}</button>
                </td>
                <td className="px-4 py-2.5">
                  <Badge c={l.method === "upi" ? C.blue : C.plum} bg={l.method === "upi" ? C.blueBg : C.plumBg}>{l.method.toUpperCase()}</Badge>
                </td>
                <td className="px-4 py-2.5 text-xs" style={{ ...mono, color: C.sub }}>{l.method === "upi" ? l.upi : "—"}</td>
                <td className="px-4 py-2.5">
                  <input type="number" disabled={!canEdit} className={inputCls} style={{ ...inputSty, width: 110, ...mono }}
                    value={week.entries[l.id] || ""} placeholder="0" onChange={e => setAmt(l.id, e.target.value)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {canEdit && (
          <div className="flex flex-wrap items-end gap-2 p-3" style={{ borderTop: `1px solid ${C.line}`, background: C.bg }}>
            <Field label="Name"><input className={inputCls} style={{...inputSty,width:130}} value={lf.name} onChange={e=>setLf({...lf,name:e.target.value})} /></Field>
            <Field label="Method"><select className={inputCls} style={{...inputSty,width:90}} value={lf.method} onChange={e=>setLf({...lf,method:e.target.value})}><option value="upi">UPI</option><option value="cash">Cash</option></select></Field>
            {lf.method === "upi" && <Field label="UPI ID"><input className={inputCls} style={{...inputSty,width:150}} value={lf.upi} onChange={e=>setLf({...lf,upi:e.target.value})} placeholder="name@bank" /></Field>}
            <Btn icon={Plus} onClick={() => { if(!lf.name)return; createLabour(lf); setLf({name:"",method:"upi",upi:""}); }}>Add labourer</Btn>
          </div>
        )}
      </Card>
      <p className="mt-3 text-xs" style={{ color: C.sub }}>
        Pattern: one Saturday is <b>kharchi</b> (small sustenance amount), the next is full <b>wages</b> by work done.
        Staff fill amounts each Saturday; you download the sheet with each labourer's payment method (UPI ID or cash) to pay out.
      </p>
    </div>
  );
}
